/**
 * Conversation block. The learner types Portuguese; the tutor corrects,
 * explains briefly, and continues in character.
 *
 * Needs a network connection. When offline the block renders a clear inline
 * state and the rest of the session stays fully usable — a block that cannot
 * run does not block the ones that can (docs/04).
 */

import { api, ApiError } from "../api.ts";
import { play } from "../audio.ts";
import type { AttemptRecord, ConversationPayload, TurnHistoryEntry, TurnResponse } from "../types.ts";
import { el, gloss, primaryButton, pt } from "../ui.ts";
import { diffCorrection } from "./diff.ts";
import { uuid, type BlockContext, type RenderedBlock } from "./types.ts";

export function renderConversation(context: BlockContext): RenderedBlock {
	const payload = context.block.payload as unknown as ConversationPayload;
	const roundsTotal = Math.max(1, payload.rounds);
	const attempts: AttemptRecord[] = [];
	const history: TurnHistoryEntry[] = [];
	let round = 1;
	let busy = false;

	const body: Node[] = [];

	if (payload.scenario_en.trim() !== "") {
		body.push(el("div", { class: "meta", text: payload.scenario_en }));
	}
	if (payload.goal_en.trim() !== "") {
		const goal = gloss(payload.goal_en, context.englishRatio);
		if (goal !== null) body.push(goal);
	}

	const thread = el("div", { class: "stack--tight", "aria-live": "polite" });
	body.push(thread);

	appendTutor(payload.opener_pt, payload.opener_en, "");
	history.push({ role: "tutor", text: payload.opener_pt });

	const input = el("textarea", {
		lang: "pt-BR",
		rows: "2",
		placeholder: "Escreva sua resposta…",
		"aria-label": "Your reply in Portuguese",
	});
	const composer = el("div", { class: "composer" }, input);
	body.push(composer);

	const status = el("div", { class: "stack--tight" });
	body.push(status);

	if (!context.online) {
		status.append(
			el("div", {
				class: "notice",
				text: "Needs a connection. Skip for now and come back to it — the rest of the session still works.",
			}),
		);
		input.disabled = true;
	}

	const send = async (): Promise<void> => {
		const text = input.value.trim();
		if (text === "" || busy) return;
		busy = true;
		input.disabled = true;
		updateFooter();

		const turn = appendLearner(text);
		history.push({ role: "learner", text });
		input.value = "";
		status.replaceChildren();

		try {
			const response = await api.turn(context.session.id, {
				block_id: context.block.id,
				text,
				round,
				history: history.slice(0, -1),
			});
			handleTurn(response, text);
		} catch (error) {
			// Undo the optimistic append. Leaving the failed line in the transcript
			// would send the next request two learner turns in a row with nothing
			// between them, and the tutor then answers the earlier one.
			history.pop();
			turn.remove();
			input.value = text;

			const message =
				error instanceof ApiError
					? error.message
					: "That did not go through. Your answer is still in the box — try again in a moment.";
			// A failed tutor call must not trap the learner in the block: offer
			// the way out alongside the error.
			status.replaceChildren(
				el("div", { class: "notice" }, message),
				el("button", {
					class: "link",
					type: "button",
					text: "Skip this block",
					onclick: () => context.finish({ attempts }),
				}),
			);
		} finally {
			busy = false;
			input.disabled = round > roundsTotal;
			updateFooter();
			scrollToLatest();
		}
	};

	function handleTurn(response: TurnResponse, learnerText: string): void {
		if (response.assessment !== "correct" && response.corrected_pt.trim() !== "") {
			thread.append(correctionCard(response, learnerText));
		}
		if (response.reply_pt.trim() !== "") {
			appendTutor(response.reply_pt, response.reply_en, response.reply_audio_url);
			// The correction rides along with the reply: the server replays these
			// as the tutor's own past turns, and without it the model cannot tell
			// that this line was already corrected.
			history.push({
				role: "tutor",
				text: response.reply_pt,
				assessment: response.assessment,
				corrected: response.corrected_pt,
				explanation: response.explanation_en,
			});
		}

		attempts.push({
			id: uuid(),
			session_id: context.session.id,
			block_id: context.block.id,
			item_id: null,
			modality: "production",
			correct: response.assessment === "correct",
			confidence: null,
			response_text: learnerText,
			elapsed_ms: null,
			created_at: Date.now(),
		});

		// The tutor answered with a correction but said nothing. The scene has
		// not moved, so the round is not spent: nothing was added to the history
		// either, and sending again with a round already used would leave two
		// learner turns adjacent — which is what makes the next correction land
		// on the wrong sentence.
		if (response.reply_pt.trim() === "") {
			status.replaceChildren(
				el("div", { class: "notice", text: "The tutor did not answer that one." }),
				el("button", {
					class: "link",
					type: "button",
					text: "Ask again",
					onclick: () => {
						input.value = learnerText;
						status.replaceChildren();
						void send();
					},
				}),
			);
			return;
		}

		round += 1;
		status.replaceChildren();
		if (round > roundsTotal) {
			input.disabled = true;
			input.placeholder = "Scene complete";
		}
	}

	function appendTutor(text: string, glossText: string, audioUrl: string): void {
		const card = el("div", { class: "card" }, pt(text));
		if (glossText.trim() !== "") {
			const line = gloss(glossText, context.englishRatio);
			if (line !== null) card.append(line);
		}
		if (audioUrl !== "") {
			card.append(
				el("button", {
					class: "link",
					type: "button",
					text: "Listen",
					onclick: () => {
						play(audioUrl);
					},
				}),
			);
		}
		thread.append(el("div", { class: "turn" }, card));
	}

	function appendLearner(text: string): HTMLElement {
		const turn = el("div", { class: "turn turn--me" }, el("div", { class: "card" }, pt(text)));
		thread.append(turn);
		return turn;
	}

	/** Keeps the newest line in view once the keyboard has taken the lower half. */
	function scrollToLatest(): void {
		thread.lastElementChild?.scrollIntoView({ block: "nearest" });
	}

	function correctionCard(response: TurnResponse, learnerText: string): HTMLElement {
		const label = response.assessment === "minor" ? "Almost — one small fix" : "Not quite";
		const card = el("div", { class: "correction" }, el("div", { class: "meta", text: label }));

		// Only the changed word is highlighted. If the correction touches two
		// things, the tutor picked the wrong one (docs/05).
		card.append(highlightedCorrection(learnerText, response.corrected_pt));

		if (response.explanation_en.trim() !== "") {
			card.append(el("p", { class: "en en--ink", style: "margin-top:7px", lang: "en", text: response.explanation_en }));
		}
		return card;
	}

	const sendButton = primaryButton("Send", () => {
		void send();
	});

	/**
	 * One Send button for the life of the block, reused rather than rebuilt.
	 * Building a fresh one per turn left `send` holding the previous, detached
	 * button — so from the second turn on it disabled nothing and relabelled
	 * nothing, and a tap gave no sign the app had heard it.
	 */
	function updateFooter(): void {
		if (round > roundsTotal) {
			context.setFooter(primaryButton("Continue", () => context.finish({ attempts })));
			return;
		}
		sendButton.disabled = busy || !context.online;
		sendButton.textContent = busy ? "Sending…" : "Send";
		context.setFooter(sendButton);
	}

	input.addEventListener("keydown", (event) => {
		const keyboard = event as KeyboardEvent;
		if (keyboard.key === "Enter" && (keyboard.metaKey || keyboard.ctrlKey)) {
			event.preventDefault();
			void send();
		}
	});

	// Offline, the learner can still move past this block.
	const initialFooter = context.online
		? sendButton
		: primaryButton("Skip for now", () => context.finish({ attempts }));

	return { body, footer: initialFooter };
}

/**
 * Diffs the learner's sentence against the correction and marks the changed
 * words in jade. Portuguese is never italicised except here, where a
 * correction highlights a single changed word (docs/05).
 */
export function highlightedCorrection(learner: string, corrected: string): HTMLElement {
	const line = el("p", { class: "pt", lang: "pt-BR" });
	for (const piece of diffCorrection(learner, corrected)) {
		line.append(
			piece.changed
				? el("span", { class: "correction__fix", text: piece.text })
				: document.createTextNode(piece.text),
		);
	}
	return line;
}
